import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LogIn } from "lucide-react";
import { useAuth } from "@/components/site/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      navigate("/admin");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-300 rounded-sm px-4 py-3 text-base text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF5A00] focus:border-[#FF5A00] transition-shadow duration-200";

  return (
    <div className="min-h-screen bg-[#0F172A] noise-overlay flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" data-testid="login-logo-link">
            <img src="/assets/aegis-logo-dark.webp" alt="Aegis Subsurface LLC logo" className="h-24 w-auto inline-block" />
          </Link>
          <h1 className="mt-6 font-heading text-2xl font-black uppercase tracking-tight text-white" data-testid="login-heading">
            Admin Login
          </h1>
          <p className="mt-2 text-sm text-slate-400">Sign in to view quote requests</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-sm p-8 border border-slate-200" data-testid="login-form">
          {error && (
            <div className="mb-5 bg-red-50 border-l-4 border-red-600 px-4 py-3 text-sm text-red-700" data-testid="login-error">
              {error}
            </div>
          )}
          <label htmlFor="login-email" className="block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2">
            Email
          </label>
          <input
            id="login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            placeholder="you@example.com"
            data-testid="login-email-input"
          />
          <div className="flex items-center justify-between mt-5 mb-2">
            <label htmlFor="login-password" className="block text-xs font-bold uppercase tracking-wider text-[#0F172A]">
              Password
            </label>
            <Link to="/forgot-password" className="text-xs font-semibold text-[#FF5A00] hover:text-[#E04F00]" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </div>
          <input
            id="login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            placeholder="••••••••"
            data-testid="login-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wider px-8 py-3.5 rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#0F172A]"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-400">
          <Link to="/" className="hover:text-[#FF5A00] transition-colors" data-testid="login-back-link">
            ← Back to website
          </Link>
        </p>
      </div>
    </div>
  );
}
